import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  ClipboardDocumentIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { savedPaymentMethodsAPI } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import { useAuth } from '../../context/AuthContext';

export default function SavedPaymentMethodsPanel({
  customerId,
  portalToken,
  portalEmail,
}: {
  customerId?: string;
  portalToken?: string;
  portalEmail?: string;
}) {
  const { user } = useAuth();
  const [sessionRejected, setSessionRejected] = useState(false);
  const verifiedSession = useMemo(() => {
    if (customerId || portalToken) return null;
    try {
      const session = JSON.parse(
        localStorage.getItem(`modal_customer_portal:${window.location.host}`) ||
          'null'
      );
      return session?.portalToken &&
        session?.email?.toLowerCase() === user?.email?.toLowerCase() &&
        new Date(session.expiresAt).getTime() > Date.now()
        ? session
        : null;
    } catch {
      return null;
    }
  }, [customerId, portalToken, user?.email]);
  const verifiedToken = portalToken || verifiedSession?.portalToken;
  const verificationRequired =
    !customerId && (!verifiedToken || sessionRejected);
  const api = savedPaymentMethodsAPI({
    customerId,
    portalToken: verifiedToken,
  });
  const client = useQueryClient();
  const key = [
    'saved-payment-methods',
    window.location.host,
    customerId || 'self',
    user?.id,
    verifiedToken ? portalEmail || verifiedSession?.email : 'unverified',
    verifiedToken,
  ];
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(
    null
  );
  const query = useQuery(
    key,
    () => api.list().then((response) => response.data),
    {
      retry: false,
      enabled: !verificationRequired,
      onError: (failure: any) => {
        if (!customerId && failure?.response?.status === 401) {
          localStorage.removeItem(
            `modal_customer_portal:${window.location.host}`
          );
          setSessionRejected(true);
        }
      },
    }
  );
  const refresh = () => client.invalidateQueries(key);
  const error = (failure: any) => {
    if (!customerId && failure?.response?.status === 401) {
      localStorage.removeItem(`modal_customer_portal:${window.location.host}`);
      setSessionRejected(true);
    }
    toast.error(
      failure?.response?.data?.message ||
        'Payment method change could not be completed. Retry to reconcile it.'
    );
  };
  const create = useMutation(api.createLink, {
    onSuccess: (response) => {
      if (customerId) {
        setLink(response.data);
        refresh();
      } else window.location.assign(response.data.url);
    },
    onError: error,
  });
  const change = useMutation(
    ({
      action,
      id,
    }: {
      action: 'remove' | 'setDefault' | 'cancel';
      id: string;
    }) => api[action](id),
    {
      onSuccess: () => {
        setLink(null);
        refresh();
        toast.success('Payment methods updated.');
      },
      onError: (failure) => {
        refresh();
        error(failure);
      },
    }
  );
  const [verification, setVerification] = useState<any>(null);
  const verify = useMutation(api.verification, {
    onSuccess: (response) => {
      setVerification(response.data);
      refresh();
    },
    onError: error,
  });
  const busy = create.isLoading || change.isLoading || verify.isLoading;
  const data = query.data;
  if (verificationRequired)
    return (
      <section
        className="rounded-card border border-outline/60 bg-surface p-4 sm:p-5"
        aria-label="Saved payment methods"
      >
        <h2 className="text-lg font-semibold text-on-surface">
          Saved payment methods
        </h2>
        <p className="mt-2 text-sm text-on-surface-variant">
          Verify your email to securely manage payment methods saved with your
          operator.
        </p>
        <a className="btn-primary mt-4 inline-block" href="/customer/access">
          Verify email
        </a>
      </section>
    );
  return (
    <section
      className="rounded-card border border-outline/60 bg-surface p-4 sm:p-5"
      aria-label="Saved payment methods"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-on-surface">
            Saved payment methods
          </h2>
          <p className="mt-1 text-sm text-on-surface-variant">
            Payment methods saved with this operator. Adding one does not take a
            payment.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !data?.ready}
            onClick={() => create.mutate('card')}
          >
            {create.isLoading
              ? 'Preparing…'
              : customerId
              ? 'Request card setup'
              : 'Add card'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={busy || !data?.achReady}
            title={data?.achUnavailableReason || 'Add a US bank account'}
            onClick={() => create.mutate('us_bank_account')}
          >
            {customerId ? 'Request bank setup' : 'Add bank account'}
          </button>
        </div>
      </div>
      {query.isLoading ? (
        <div className="py-5">
          <LoadingSpinner size="sm" />
        </div>
      ) : query.isError ? (
        <div className="mt-4 text-sm text-on-surface-variant" role="alert">
          {(query.error as any)?.response?.data?.message ||
            'Saved payment methods are unavailable.'}
          <button
            type="button"
            className="ml-3 underline"
            onClick={() => query.refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {!data?.ready && (
            <p className="mt-4 text-sm text-on-surface-variant" role="status">
              {data?.unavailableReason}
            </p>
          )}
          {!data?.achReady && data?.achUnavailableReason && (
            <p className="mt-3 text-xs text-on-surface-variant">
              {data.achUnavailableReason}
            </p>
          )}
          <div className="mt-4 divide-y divide-outline/60">
            {(data?.methods || []).map((method: any) => (
              <div
                key={method.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div className="flex min-w-0 items-center gap-3">
                  {method.type === 'us_bank_account' ? (
                    <BuildingLibraryIcon className="h-6 w-6 shrink-0 text-primary" />
                  ) : (
                    <CreditCardIcon className="h-6 w-6 shrink-0 text-primary" />
                  )}
                  <div>
                    <p className="font-medium text-on-surface">
                      {method.type === 'us_bank_account'
                        ? method.bankName || 'US bank account'
                        : method.brand.toUpperCase()}{' '}
                      ending in {method.last4}
                      {method.isDefault && (
                        <span className="ml-2 text-xs text-primary">
                          Default
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-on-surface-variant">
                      {method.type === 'us_bank_account'
                        ? method.verified
                          ? 'Verified bank account'
                          : 'Bank authorization needs review'
                        : `Expires ${method.expMonth}/${method.expYear}`}
                      {method.pendingAction ? ' · Update awaiting retry' : ''}
                    </p>
                    {method.authorization === 'existing_method' && (
                      <p className="text-xs text-on-surface-variant">
                        Existing Stripe method; no new automatic-charge
                        authorization.
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  {!method.isDefault && (
                    <button
                      type="button"
                      title="Make default"
                      aria-label={`Make ${
                        method.type === 'us_bank_account'
                          ? 'bank account'
                          : 'card'
                      } ending in ${method.last4} default`}
                      disabled={
                        busy ||
                        method.authorization === 'revoking' ||
                        method.authorization === 'revoked' ||
                        (method.type === 'us_bank_account' && !method.verified)
                      }
                      className="rounded-md border border-outline p-2 disabled:opacity-40"
                      onClick={() =>
                        change.mutate({ action: 'setDefault', id: method.id })
                      }
                    >
                      <StarIcon className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    type="button"
                    title="Remove payment method"
                    aria-label={`Remove ${
                      method.type === 'us_bank_account'
                        ? 'bank account'
                        : 'card'
                    } ending in ${method.last4}`}
                    disabled={busy}
                    className="rounded-md border border-outline p-2 disabled:opacity-40"
                    onClick={() => {
                      if (
                        window.confirm(
                          'Remove this saved payment method? It will no longer be available for future payments. Past payments and pending debits are unchanged.'
                        )
                      )
                        change.mutate({ action: 'remove', id: method.id });
                    }}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
            {!data?.methods?.length && (
              <p className="py-4 text-sm text-on-surface-variant">
                No saved payment methods.
              </p>
            )}
          </div>
          {link && (
            <div className="mt-4 border-t border-outline/60 pt-4">
              <label
                className="block text-sm font-medium"
                htmlFor="saved-card-setup-link"
              >
                Customer setup link
              </label>
              <div className="mt-2 flex gap-2">
                <input
                  id="saved-card-setup-link"
                  readOnly
                  value={link.url}
                  className="input-field min-w-0 flex-1"
                />
                <button
                  type="button"
                  title="Copy setup link"
                  aria-label="Copy setup link"
                  className="rounded-md border border-outline p-2"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(link.url);
                      toast.success('Setup link copied.');
                    } catch {
                      toast.error(
                        'Copy unavailable. Select the setup link to copy it.'
                      );
                    }
                  }}
                >
                  <ClipboardDocumentIcon className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-2 text-xs text-on-surface-variant">
                Customer-entered payment details only. Link expires{' '}
                {new Date(link.expiresAt).toLocaleString()}.
              </p>
            </div>
          )}
          {(data?.setups || [])
            .filter((setup: any) => setup.canCancel)
            .map((setup: any) => (
              <div
                key={setup.id}
                className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-outline/60 pt-3 text-sm"
              >
                <span>
                  {setup.type === 'us_bank_account' ? 'Bank' : 'Card'} setup ·{' '}
                  {setup.status.replace(/_/g, ' ')} ·{' '}
                  {new Date(setup.expiresAt).getTime() <= Date.now()
                    ? 'Expired'
                    : 'Expires'}{' '}
                  {new Date(setup.expiresAt).toLocaleString()}
                </span>
                {setup.canVerify && (
                  <button
                    type="button"
                    className="underline"
                    disabled={busy}
                    onClick={() => verify.mutate(setup.id)}
                  >
                    Check / resume verification
                  </button>
                )}
                <button
                  type="button"
                  className="underline"
                  disabled={busy}
                  onClick={() =>
                    change.mutate({ action: 'cancel', id: setup.id })
                  }
                >
                  Cancel setup
                </button>
              </div>
            ))}
          {verification && (
            <div
              className="mt-4 border-t border-outline/60 pt-4 text-sm"
              role="status"
            >
              <p>
                {verification.status === 'succeeded'
                  ? 'Bank account verified and saved. No payment was taken.'
                  : "Bank verification is pending. Follow Stripe's verification instructions; no payment was taken."}
              </p>
              {verification.verificationUrl && (
                <a
                  href={verification.verificationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-block text-primary underline"
                >
                  Open customer bank verification
                </a>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
